<template>
  <SidebarMenu
    v-if="isMenuReady"
    v-model:collapsed="isCollapsed"
    :relative="true"
    :menu="menu"
    :show-child="collapsed"
    :show-one-child="true"
    :theme="theme"
    width-collapsed="43px"
    style="z-index: 16"
    @item-click="onItemClick"
    @toggle-collapse="switchCollapse"
  >
    <template #toggle-icon>
      <div
        style="
          display: flex;
          align-items: flex-start;
          justify-content: space-evenly;
          flex-direction: column;
          width: 100%;
          height: 100%;
          padding-left: 0.5rem;
          padding-top: 1rem;
        "
      >
        <i class="fas fa-bug" style="opacity: 0" />
        <i
          class="fas"
          :class="{
            'fa-chevron-left': !isCollapsed,
            'fa-chevron-right': isCollapsed,
          }"
        />
      </div>
    </template>
  </SidebarMenu>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { SidebarMenu, type SidebarItem } from "vue-sidebar-menu";
import { useTranslation } from "@/language/translation";
import { useStore } from "vuex";
import { useUserMenu, type MenuEntryType } from "@/functions/useUserMenu";
import "vue-sidebar-menu/dist/vue-sidebar-menu.css";
const emit = defineEmits(["openCommandMenu"]);
const { t } = useTranslation();
const collapsed = ref(true);
const isCollapsed = ref(true);

const store = useStore();

const theme = computed(() => {
  return store.getters["login/showDarkTheme"] ? undefined : ("white-theme" as const);
});

function switchCollapse(collapsedProp: boolean) {
  collapsed.value = collapsedProp;
}
function onItemClick(event: Event, item: SidebarItem) {
  const parsedItem = item as MenuEntryType;
  if (parsedItem.callback != null) {
    parsedItem.callback(event, parsedItem);
  }
}
const mac = navigator.userAgent.includes("Mac");
const { menu: menuWithoutSearch } = useUserMenu();

const menu = computed<MenuEntryType[]>(() => {
  const entries: MenuEntryType[] = [];
  entries.push({
    icon: "fas fa-magnifying-glass",
    title: t("global.search"),
    badge: mac
      ? {
          text: mac ? "⌘ + K" : `${t("modules.commandMenu.views.CommandPlaceholder.ctrl")} + K`,
          class: "vsm--badge_default",
        }
      : undefined,
    callback(event: Event, item: MenuEntryType): void {
      emit("openCommandMenu", event, item);
    },
  });
  return [...entries, ...menuWithoutSearch.value];
});
const isMenuReady = computed(() => {
  return Array.isArray(menu.value) && menu.value.length > 0;
});
</script>

<style lang="scss">
.v-sidebar-menu.vsm_white-theme {
  background: #fafafa !important;
  border-color: #e9ecef;
  border-style: solid;
  border-top: 0;
  border-left: 0;
  border-bottom: 0;
  border-right-width: 0.5px;
}

.vsm--menu > .vsm--item > .vsm--link {
  height: 2.1875rem;
}

.vsm_collapsed,
.vsm_expanded {
  .vsm--item {
    .vsm--dropdown {
      .vsm--item {
        .vsm--link_level-2 {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          border-bottom: 1px solid lightgrey;

          .vsm--title {
            font-size: 0.75rem;
          }
        }

        .vsm--dropdown {
          padding-top: 0 !important;

          .vsm--link_level-3 {
            padding-top: 0 !important;
            padding-bottom: 0 !important;

            .vsm--title {
              font-size: 0.875rem;
            }
          }
        }
      }
    }
  }

  .vsm--link_level-2 {
    .vsm--arrow_open {
      display: none;
    }
  }
}

.v-sidebar-menu.vsm_white-theme .vsm--link_level-1 .vsm--icon {
  color: #707070 !important;
  height: 30px;
  width: 30px;
}

.v-sidebar-menu.vsm_white-theme.vsm_expanded .vsm--item_open .vsm--link_level-1 .vsm--icon {
  color: white !important;
}

.v-sidebar-menu.vsm_white-theme.vsm_expanded .vsm--item_open.vsm--link_level-1 {
  color: white !important;
}

.v-sidebar-menu .vsm--link {
  font-size: 0.875rem !important;
  padding: 0.25rem !important;
}

.v-sidebar-menu .vsm--link.vsm--link_level-3 {
  padding-left: 25px !important;
}

.v-sidebar-menu.vsm_white-theme .vsm--icon {
  font-size: 24px !important;
  background: transparent !important;
}

.v-sidebar-menu.vsm_white-theme .vsm--link_level-1.vsm--link_active {
  -webkit-box-shadow: 3px 0 0 0 var(--p-primary-color) inset !important;
  box-shadow: 3px 0 0 0 var(--p-primary-color) inset !important;
}

.v-sidebar-menu.vsm_white-theme.vsm_collapsed .vsm--link_level-1.vsm--link_hover .vsm--icon,
.v-sidebar-menu.vsm_white-theme.vsm_collapsed .vsm--link_level-1:hover .vsm--icon {
  color: white !important;
}

.v-sidebar-menu.vsm_white-theme .vsm--link_level-1.vsm--link_exact-active,
.v-sidebar-menu.vsm_white-theme .vsm--link_level-1.vsm--link_active {
  box-shadow: 3px 0 0 0 var(--p-primary-color) inset !important;
}

.v-sidebar-menu .vsm--toggle-btn {
  height: 5.3rem !important;
}

.vsm--link {
  transition: none !important;
}

.slide-animation-enter-active,
.slide-animation-leave-active,
.expand-enter-active,
.expand-leave-active,
.fade-animation-enter-active,
.fade-animation-leave-active {
  transition: none !important;
}

.v-sidebar-menu {
  --vsm-primary-color: var(--p-primary-color);
}

.vsm_collapsed .vsm--menu > li > div > ul {
  // Adds space after the last item in the collapsed menu to prevent entry lying behind the chrome link preview
  padding-bottom: 1rem !important;
}
</style>
